import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Row, Col, ButtonGroup, Button } from 'reactstrap';
import { Link } from 'react-router-dom';
import PageTitle from '../../../components/PageTitle/PageTitle';
import { Host } from '../../../models';
import { resetHosts, resetCollectors, resetPrinters, resetEfts } from '../../../redux';
import { loadHosts, loadCollectors, loadPrinters, loadEfts } from '../../../redux';
import Icon from '@mdi/react';
import { mdiFormatListText, mdiRefresh } from '@mdi/js';
import { HostResume, CollectorResume, PrinterResume, EftResume } from '../../../components';
import Diagram, { createSchema } from 'beautiful-react-diagrams';

import 'beautiful-react-diagrams/styles.css';
import './Map.css';

const Map = () => {
    const { t }    = useTranslation();
    const dispatch = useDispatch();

    const { connected }  = useSelector(state => state.sioBridge);
    const { hosts }      = useSelector(state => state.hosts);
    const { collectors } = useSelector(state => state.collectors);
    const { printers }   = useSelector(state => state.printers);
    const { efts }       = useSelector(state => state.efts);

    const refresh = () => {
        if (connected) {
            dispatch(resetHosts());
            dispatch(resetCollectors());
            dispatch(resetPrinters());
            dispatch(resetEfts());

            dispatch(loadHosts());
            dispatch(loadCollectors());
            dispatch(loadPrinters());
            dispatch(loadEfts());
        }
    };
    useEffect(refresh, [dispatch, connected]);

    const { innerWidth: width } = window;
    const hostWidth = 200;
    const hostHeight = 235;
    const deviceWidth = 180;
    const deviceHeight = 210;
    const devicesGap = 50;
    const canvasPadding = 20;
    let x = 0;
    let y = 20;
    let nbDevicesOnRow = 0;
    let maxNbDevicesOnRow = parseInt((width - 250) / 280, 10);
    let reachedMaxNbDevicesOnRow = 0;
    let nbRows = 1;

    const deviceContent = device => {
        switch (device.type) {
            case 'collector':
                return <CollectorResume hideHost hideDetails collector={ device } style={{ width: deviceWidth + 'px' }} />
            case 'printer':
                return <PrinterResume hideHost hideDetails printer={ device } style={{ width: deviceWidth + 'px' }} />
            case 'eft':
                return <EftResume hideHost hideDetails eft={ device } style={{ width: deviceWidth + 'px' }} />
        }
    }

    const devices = [].concat(printers || [], efts || []).filter(d => d.wasRecentlyConnected());
    const orphans = [];
    devices.forEach(device => {
        const { _id, name } = device.getHostInfos();
        let host = hosts?.find(h => h._id === _id);
        if (!host)
            return orphans.push(device);

        if (host.status !== 'connected' && device.status === 'connected')
            host.status = 'connected';

        if (host)
            host.connectDevice(device);
    });

    const nodes = [];
    const links = [];

    orphans.forEach((device, i) => {
        x = Math.max(x, 20);
        nodes.push({
            id: device._id,
            content: deviceContent(device),
            coordinates: [x, y]
        });
        x += deviceWidth + devicesGap;

        nbDevicesOnRow += 1;

        if (nbDevicesOnRow >= maxNbDevicesOnRow) {
            if (nbDevicesOnRow > reachedMaxNbDevicesOnRow)
                reachedMaxNbDevicesOnRow = nbDevicesOnRow;
            x = 0;
            y += deviceHeight + devicesGap;
            nbDevicesOnRow = 0;
            nbRows ++;
        }
    });
    nbDevicesOnRow = maxNbDevicesOnRow;

    hosts.forEach((host, i) => {
        const connectedDevices = host.getConnectedDevices();
        const centerX = (connectedDevices.length || 1) * (deviceWidth + devicesGap) / 2 - hostWidth / 2 - 5;
        x += centerX;
        nodes.push({
            id: host._id,
            content: <HostResume hideDetails host={ host } style={{ width: `${hostWidth}px` }}/>,
            coordinates: [x, y],
        });

        x += canvasPadding - centerX;
        y += hostHeight + devicesGap;
        connectedDevices.forEach(device => {
            nodes.push({
                id: device._id,
                content: deviceContent(device),
                coordinates: [x, y]
            });
            links.push({ input: host._id, output: device._id, className: device.isConnected() ? 'connected' : 'disconnected' });
            x += deviceWidth + devicesGap;
        });
        if (!connectedDevices?.length)
            x += deviceWidth + devicesGap;
        nbDevicesOnRow += connectedDevices.length || 1;

        if (nbDevicesOnRow >= maxNbDevicesOnRow) {
            if (nbDevicesOnRow > reachedMaxNbDevicesOnRow)
                reachedMaxNbDevicesOnRow = nbDevicesOnRow;
            if (i < Object.values(hosts).length - 1) {
                x = 0;
                y += deviceHeight + devicesGap;
                nbDevicesOnRow = 0;
                nbRows ++;
            }
        } else {
            y -= (hostHeight + devicesGap);
        }
    });

    const schema = createSchema({ nodes, links });

    const actions = (
        <>
            <Button color="primary" onClick={refresh} className="mr-2">
                <Icon path={ mdiRefresh } size={ 1 } color="white" />
            </Button>
            <Link to="/devices/list">
                <Button color="info">
                    <Icon path={ mdiFormatListText } size={ 1 } color="white" /> { t('devices.list') }
                </Button>
            </Link>
        </>
    );

    const canvasDims = {
        width: `${reachedMaxNbDevicesOnRow * (deviceWidth + devicesGap) + 20}px`,
        minWidth: '100%',
        height: `${nbRows * (hostHeight + deviceHeight + 2 * devicesGap)}px`
    };

    const collectorStyle = {
        width: '200px',
        float: 'left',
        marginRight: '50px'
    };

    return (
        <div className="Map">
            <PageTitle
                title={ t('devices.title') }
                icon="cubes"
                actions= {actions }
            />
            <div style={ canvasDims }>
                { schema && (
                    <Diagram schema={schema} />
                )}
            </div>
            { collectors && collectors.length > 0 && (
            <>
                <br />
                <h4>
                    <i className="fa fa-money" />{' '}
                    {t('collectors.title')}
                </h4>
                <Row>
                    <Col>
                    { collectors.filter(c => c.isConnected()).map(collector => (
                        <CollectorResume key={ collector.id } collector={ collector } className="mb-3" style={ collectorStyle } />
                    ))}
                    { collectors.filter(c => !c.isConnected()).map(collector => (
                        <CollectorResume key={ collector.id } collector={ collector } className="mb-3" style={ collectorStyle } />
                    ))}
                    </Col>
                </Row>
            </>
            )}
        </div>
    );
}

export default Map;
